//
// # AiAssistant
//
// Right-panel AI chat assistant.
// Sends prompts to AlphaServer via workspace.sendPrompt() and renders the
// assistant's answer purely from the streaming WebSocket channel, routed to this
// conversation by conversationId (§14.10). The chat subscribes ONCE (persistent
// per-conversation subscription) rather than registering a callback per prompt.
// Per §14.9 prompt() is non-blocking — it returns an empty ack almost
// immediately and the answer (and intermediate progress) arrives as later WS
// frames — so we do NOT read the answer from the HTTP response body.
//
// Limitation: Only one prompt can be in flight at a time.
// There is no prompt queue — input is disabled while waiting for a response.
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "github-markdown-css/github-markdown-dark.css";
// Light-theme table overrides (must come after the dark stylesheet so they win).
import "./AiAssistant.css";
import {
  Loader2,
  Mic,
  Paperclip,
  Send,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import type { ImageAttachment } from "ServerInterface";
import type { AgentTreeNode } from "AgentRunTreeSchema";
import { useWorkspace } from "../../context";
import { useAgentActivity } from "../AgentActivity/agentActivity";
import {
  AgentActivityIndicator,
  AgentStatusStrip,
} from "../AgentActivity/AgentActivityIndicator";

// Input area sizing constants (in pixels)
const DEFAULT_INPUT_HEIGHT = 25; // Initial height of the text input area
const MIN_DRAG_HEIGHT = 25; // Minimum height when drag-resizing
const MAX_INPUT_HEIGHT = 400; // Maximum height the input area can grow to
const MAX_PROMPT_HISTORY = 100; // Limit stored prompts to avoid unbounded growth
// Within this many px of the bottom counts as "stuck to bottom" — streaming
// frames auto-scroll only when the user is already there.
const NEAR_BOTTOM_PX = 80;

// Keys that shouldn't exit history navigation mode (modifiers, horizontal cursor movement)
const NON_HISTORY_EXITING_KEYS = new Set([
  "Shift",
  "Control",
  "Alt",
  "Meta",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "CapsLock",
]);

type AttachedImage = Readonly<ImageAttachment> & {
  readonly id: string;
  readonly previewUrl: string; // data URL for <img> display only
};

// Max size per image in bytes (base64 encoded). ~4MB leaves headroom in 5MB body limit.
const MAX_IMAGE_BASE64_LENGTH = 4 * 1024 * 1024;
const MAX_ATTACHED_IMAGES = 5;
const MAX_TOTAL_IMAGE_PAYLOAD = 10 * 1024 * 1024; // 10MB total across all images

const MIME_TO_IMAGE_KIND: Record<string, "png" | "jpeg" | "gif"> = {
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/gif": "gif",
};

interface Message {
  // "activity" is a streamed progress line (tool call / commentary) — a dim,
  // append-only log line, distinct from the durable user/assistant bubbles.
  readonly role: "user" | "assistant" | "activity";
  readonly text: string;
  readonly imageUrls?: readonly string[]; // Preview URLs for user-attached images
}

// Rotating "still working" words shown while a turn is in flight, à la Claude
// Code. Purely cosmetic; cycles every THINKING_WORD_INTERVAL_MS.
const THINKING_WORDS = [
  "Ideating",
  "Vibing",
  "Cooking",
  "Percolating",
  "Scheming",
  "Manifesting",
  "GenAlpha-ing",
  "Noodling",
  "Conjuring",
  "Tinkering",
  "Finagling",
  "Galaxy-braining",
  "Transistor-wrangling",
  "Routing good vibes",
] as const;
const THINKING_WORD_INTERVAL_MS = 2000;

// True while any agent in the tree is still working (running, or idle awaiting
// active work), vs a tree where every agent is done/stopped or merely "sleeping"
// on a deferred `delay` timer. Lets the "working" indicator track the whole
// hierarchy — including sub-agents that keep going after the root has answered —
// but stay still while the tree is only waiting out a scheduled call.
//
// `turnCount > 0` is what separates "between turns, awaiting active work" from
// "constructed but never run".
function treeHasActiveAgent(node: AgentTreeNode): boolean {
  if (node.status === "running") return true;
  if (node.status === "idle" && node.turnCount > 0) return true;
  return node.children.some(treeHasActiveAgent);
}

export function AiAssistant() {
  const workspace = useWorkspace();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  // Only one prompt at a time — no queue. Input is disabled while waiting.
  const [loading, setLoading] = useState(false);
  // True for the whole agent run (send → terminal answer), unlike `loading`,
  // which only spans the brief prompt-ack round-trip (the agent runs in the
  // background and streams over WebSocket). Drives the "working" indicator.
  const [working, setWorking] = useState(false);
  // Whether we've observed real tree activity since the last send. Guards
  // `working` from being cleared by a stale settling-push before the run spins up.
  const sawActivity = useRef(false);
  // Whether a cancellation has been requested (disables Stop button while aborting)
  const [cancellationRequested, setCancellationRequested] = useState(false);
  // Index into THINKING_WORDS for the rotating "still working" indicator shown
  // while a turn is in flight.
  const [thinkingIdx, setThinkingIdx] = useState(0);
  // Phase derived from `working`, adding a brief visible `done` state and an
  // explicit `idle` resting state. See agentActivity.ts.
  const agentActivity = useAgentActivity(working);
  const viewportRef = useRef<HTMLDivElement>(null);

  // Attached images for the next prompt
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Speech recognition state
  const [isRecording, setIsRecording] = useState(false);
  // Use InstanceType to avoid referencing the SpeechRecognition global directly (eslint no-undef)
  const recognitionRef = useRef<InstanceType<
    NonNullable<typeof window.SpeechRecognition>
  > | null>(null);

  // Prompt history for up/down arrow navigation (in-memory, lost on page refresh)
  const promptHistory = useRef<string[]>([]);
  const historyPosition = useRef(-1); // -1 means "not navigating"
  const historyDraft = useRef(""); // Saves current input when user starts navigating

  const scrollToBottom = () => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
    }
  };

  // Whether the transcript is scrolled to (near) the bottom. Streaming frames
  // use this to decide whether to auto-scroll — if the user has scrolled up to
  // read history, we leave their position alone. Read BEFORE appending, so it
  // reflects the pre-append scroll position.
  const isNearBottom = (): boolean => {
    const el = viewportRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };

  const appendMessage = (role: Message["role"], text: string) => {
    setMessages((prev) => [...prev, { role, text }]);
    setTimeout(scrollToBottom, 0);
  };

  // Subscribe ONCE to this conversation's streamed progress / answer frames
  useEffect(() => {
    const unsubscribe = workspace.subscribeAssistantProgress(
      (message, kind) => {
        // Each frame is its own line: the terminal answer becomes a durable
        // assistant bubble; interim progress appends a dim activity line.
        // Only auto-scroll if the user is already at the bottom, so streaming
        // frames don't yank them away while they read scrollback.
        const stick = isNearBottom();
        const role = kind === "answer" ? "assistant" : "activity";
        setMessages((prev) => [...prev, { role, text: message }]);
        if (stick) setTimeout(scrollToBottom, 0);
      }
    );
    return unsubscribe;
  }, [workspace]);

  // Drive "working" off the live agent tree so the indicator tracks the whole
  // hierarchy (sub-agents included), not just the root's turn. Optimistically
  // set on send (see handleSend) to cover the gap before the first push; the
  // tree then winds it down once every agent is done/stopped.
  useEffect(() => {
    return workspace.subscribeAgentTree((tree) => {
      const active = tree !== null && treeHasActiveAgent(tree);
      if (active) {
        sawActivity.current = true;
        setWorking(true);
      } else if (sawActivity.current) {
        setWorking(false);
      }
    });
  }, [workspace]);

  // Rotate the "still working" word while a turn is in flight. Starts at a
  // random word each turn so consecutive prompts don't always open the same.
  useEffect(() => {
    if (!working) return;
    setThinkingIdx(Math.floor(Math.random() * THINKING_WORDS.length));
    const id = setInterval(() => {
      setThinkingIdx((i) => (i + 1) % THINKING_WORDS.length);
    }, THINKING_WORD_INTERVAL_MS);
    return () => {
      clearInterval(id);
    };
  }, [working]);

  // Process an image File into an AttachedImage and add it to state.
  // Limit checks are inside the functional updater so concurrent
  // FileReader callbacks always see the latest state.
  const processImageFile = useCallback(
    (file: File): void => {
      const imageKind = MIME_TO_IMAGE_KIND[file.type];
      if (!imageKind) {
        appendMessage(
          "assistant",
          `Unsupported image type: ${file.type}. Please use PNG, JPEG, or GIF.`
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== "string") {
          appendMessage("assistant", `Failed to read image "${file.name}".`);
          return;
        }
        const parts = reader.result.split(",");
        if (parts.length !== 2) {
          appendMessage("assistant", `Failed to read image "${file.name}".`);
          return;
        }
        const base64 = parts[1];
        if (!base64 || base64.length > MAX_IMAGE_BASE64_LENGTH) {
          appendMessage(
            "assistant",
            `Image "${file.name}" is too large (max 4MB).`
          );
          return;
        }

        // Keep the updater pure (no side-effects inside it). Stash any
        // error in a local variable and call appendMessage after.
        let attachError: string | null = null;
        setAttachedImages((prev) => {
          if (prev.length >= MAX_ATTACHED_IMAGES) {
            attachError = `Maximum ${MAX_ATTACHED_IMAGES} images allowed.`;
            return prev;
          }
          const currentTotal = prev.reduce(
            (sum, img) => sum + img.imageEncoded.length,
            0
          );
          if (currentTotal + base64.length > MAX_TOTAL_IMAGE_PAYLOAD) {
            attachError =
              "Total image payload exceeds 10MB limit. Remove an image first.";
            return prev;
          }
          return [
            ...prev,
            {
              id: crypto.randomUUID(),
              imageKind,
              encodingKind: "base64",
              imageEncoded: base64,
              previewUrl: reader.result as string,
            },
          ];
        });
        if (attachError !== null) {
          appendMessage("assistant", attachError);
        }
      };
      reader.onerror = () => {
        appendMessage("assistant", `Failed to read image "${file.name}".`);
      };
      reader.readAsDataURL(file);
    },
    [appendMessage]
  );

  // Handle Stop button click — cancel the in-flight prompt
  const handleStop = useCallback(() => {
    if (!loading || cancellationRequested) return;
    setCancellationRequested(true);
    workspace.cancelPrompt();
  }, [loading, cancellationRequested, workspace]);

  // Reset cancellation state when loading completes
  useEffect(() => {
    if (!loading) {
      setCancellationRequested(false);
    }
  }, [loading]);

  // Toggle speech recognition on/off
  const toggleRecording = useCallback(() => {
    // Access constructors from window to avoid eslint no-undef for global types.
    // These may be undefined in browsers that don't support speech recognition.
    const SpeechRecognitionCtor =
      window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (typeof SpeechRecognitionCtor === "undefined") {
      appendMessage(
        "assistant",
        "Speech recognition is not supported in your browser. Try Chrome or Edge."
      );
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    // Track the final transcript separately from interim results
    let finalTranscript = "";

    // Let TypeScript infer event types to avoid eslint no-undef for global type names
    recognition.onresult = (event) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      // Show final + interim in the input; final portion is committed
      setInput(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event) => {
      setIsRecording(false);
      if (event.error !== "aborted") {
        appendMessage("assistant", `Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }, [isRecording, appendMessage]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && attachedImages.length === 0) || loading) return;

    // Stop any active speech recognition before sending
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }

    // Add to prompt history (avoid duplicating consecutive entries)
    const history = promptHistory.current;
    if (history.length === 0 || text !== history[0]) {
      history.unshift(text);
      if (history.length > MAX_PROMPT_HISTORY) {
        history.pop();
      }
    }
    // Reset navigation state - user is starting fresh after sending
    historyPosition.current = -1;

    // Capture attached images before clearing state
    const currentImages = attachedImages;
    const imageUrls =
      currentImages.length > 0
        ? currentImages.map((img) => img.previewUrl)
        : undefined;
    const imagesToSend =
      currentImages.length > 0
        ? currentImages.map(({ imageKind, encodingKind, imageEncoded }) => ({
            imageKind,
            encodingKind,
            imageEncoded,
          }))
        : undefined;

    setMessages((prev) => [...prev, { role: "user", text, imageUrls }]);
    setTimeout(scrollToBottom, 0);
    setInput("");
    setAttachedImages([]);
    if (!manuallyResized.current) {
      setInputHeight(DEFAULT_INPUT_HEIGHT);
    }
    setLoading(true);
    // Optimistically start the "working" indicator; the agent tree takes over
    // and winds it down once every agent is done/stopped.
    sawActivity.current = false;
    setWorking(true);

    const result = await workspace.sendPrompt(text, imagesToSend);

    if (result.err) {
      // If no run ever started (e.g. server error), clear the optimistic flag;
      // if a run did start and was cancelled, let the tree wind it down.
      // Read through a function so TS keeps the declared `boolean` type: it
      // narrows sawActivity.current to the literal `false` set before the await
      // and can't see that the agent-tree subscription may flip it true during
      // the await above.
      const sawActivityDuringSend = (): boolean => sawActivity.current;
      if (!sawActivityDuringSend()) setWorking(false);
      // User-initiated cancellation shouldn't show as an error
      if (result.val.why === "cancelled-by-user") {
        appendMessage("assistant", "Request cancelled.");
      } else {
        appendMessage("assistant", `Error: ${result.val.why}`);
      }
    }

    setLoading(false);
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputHeight, setInputHeight] = useState(DEFAULT_INPUT_HEIGHT);
  const dragging = useRef(false);
  const manuallyResized = useRef(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  // Clean up any active drag listeners if the component unmounts mid-drag
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  // Auto-grow/shrink based on scrollHeight of the textarea.
  // Temporarily collapses the textarea to measure its natural content height,
  // then sets the container to match. Skipped if the user has manually dragged the handle.
  const autoResize = useCallback(() => {
    if (manuallyResized.current) return;
    const el = textareaRef.current;
    if (!el) return;
    // Collapse to 0 so scrollHeight reflects content, not container
    el.style.height = "0px";
    const contentHeight = el.scrollHeight;
    el.style.height = "";
    // Add container padding (py-1 = 8px) + border (2px) so text isn't clipped
    const containerOverhead = 10;
    const newHeight = Math.max(
      DEFAULT_INPUT_HEIGHT,
      Math.min(contentHeight + containerOverhead, MAX_INPUT_HEIGHT)
    );
    setInputHeight(newHeight);
  }, []);

  // Drag-to-resize: mousedown on the divider starts tracking.
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      const startY = e.clientY;
      const startHeight = inputHeight;

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragging.current) return;
        manuallyResized.current = true;
        // Dragging up increases height (startY - ev.clientY is positive when moving up)
        const delta = startY - ev.clientY;
        const newHeight = Math.max(
          MIN_DRAG_HEIGHT,
          Math.min(MAX_INPUT_HEIGHT, startHeight + delta)
        );
        setInputHeight(newHeight);
      };

      const onMouseUp = () => {
        dragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        dragCleanupRef.current = null;
      };

      dragCleanupRef.current = onMouseUp;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [inputHeight]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Stop keyboard events from reaching the Editor's hotkey handlers
    e.stopPropagation();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    // Up arrow: navigate to older prompts. Only trigger history navigation if:
    // (1) cursor is at position 0 (start of first line), or
    // (2) already navigating history (so continued Up presses work)
    // This allows normal cursor movement in multi-line input.
    if (e.key === "ArrowUp") {
      const history = promptHistory.current;
      if (history.length === 0) return;

      const cursorAtStart = e.currentTarget.selectionStart === 0;
      const alreadyNavigating = historyPosition.current !== -1;
      if (!cursorAtStart && !alreadyNavigating) return;

      // Save draft when starting to navigate
      if (historyPosition.current === -1) {
        historyDraft.current = input;
      }

      if (historyPosition.current < history.length - 1) {
        e.preventDefault();
        historyPosition.current++;
        setInput(history[historyPosition.current]);
      }
      return;
    }

    // Down arrow: navigate to newer prompts
    if (e.key === "ArrowDown") {
      if (historyPosition.current === -1) return; // Not navigating

      e.preventDefault();
      if (historyPosition.current > 0) {
        historyPosition.current--;
        setInput(promptHistory.current[historyPosition.current]);
      } else {
        // Back to draft
        historyPosition.current = -1;
        setInput(historyDraft.current);
      }
      return;
    }

    // Reset history navigation for content-modifying keys, but not for
    // modifiers or horizontal cursor movement (which are useful while reviewing history)
    if (
      historyPosition.current !== -1 &&
      !NON_HISTORY_EXITING_KEYS.has(e.key)
    ) {
      historyPosition.current = -1;
    }
  };

  // Edge treatment on the panel itself, so the panel reads as "live" from
  // peripheral vision. An inset ring, so nothing reflows between phases.
  const panelRingClass = ((): string => {
    if (agentActivity.phase === "working") return "agent-panel-ring-working";
    if (agentActivity.phase === "done") return "agent-panel-ring-done";
    return "";
  })();

  return (
    <div
      // Stable hook for end-to-end tests, which otherwise have no reliable way
      // to tell whether an agent run is in flight. Renders nothing.
      data-agent-phase={agentActivity.phase}
      className={`flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)] ${panelRingClass}`}
    >
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <Sparkles size={11} />
          AI Assistant
        </div>
      </div>

      {/* Agent status strip — visible regardless of transcript scroll. */}
      <AgentStatusStrip activity={agentActivity} />

      {/* Message area */}
      <div
        ref={viewportRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden select-text"
      >
        <div className="flex flex-col gap-3 p-3 select-text">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2 text-[var(--color-ide-text-muted)]">
              <Sparkles size={20} className="opacity-40" />
              <p className="text-xs text-center">
                Ask questions about designs, PDKs, or GenAlpha
              </p>
            </div>
          ) : (
            messages.map((msg, index) => {
              // Streamed progress line — a dim log entry, not a chat bubble.
              if (msg.role === "activity") {
                return (
                  <div
                    key={index}
                    className="self-start w-full text-[11px] leading-snug text-[var(--color-ide-text-muted)] font-mono select-text break-words whitespace-pre-wrap"
                  >
                    {msg.text}
                  </div>
                );
              }
              return (
                <div
                  key={index}
                  className={
                    msg.role === "user"
                      ? "self-end max-w-[85%]"
                      : "self-start max-w-[85%]"
                  }
                >
                  <div
                    className={
                      msg.role === "user"
                        ? "rounded-lg px-3 py-2 text-xs bg-[var(--color-ide-accent)] text-white font-mono select-text cursor-text"
                        : "rounded-lg px-3 py-2 text-xs bg-[var(--color-ide-surface-2)] text-[var(--color-ide-text)] font-mono select-text cursor-text"
                    }
                  >
                    {/* Image thumbnails for user messages with attachments */}
                    {msg.imageUrls && msg.imageUrls.length > 0 && (
                      <div className="flex gap-1.5 mb-2 flex-wrap">
                        {msg.imageUrls.map((url, imgIdx) => (
                          <img
                            key={imgIdx}
                            src={url}
                            alt="Attached"
                            className="h-16 w-16 object-cover rounded border border-white/20"
                          />
                        ))}
                      </div>
                    )}
                    <div
                      className="markdown-body"
                      style={{
                        backgroundColor: "transparent",
                        color: "inherit",
                        fontSize: "inherit",
                      }}
                    >
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {msg.text}
                      </Markdown>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {/* Foot-of-transcript indicator: it reads as "the state of the
              message above it", so it only earns its place once there is a
              message. On an empty transcript it has nothing to anchor to and
              lands a few hundred pixels under the pinned strip saying the same
              thing — two identical bars that look like a rendering bug. The
              strip is the whole status surface until the first message. */}
          {messages.length > 0 && (
            <AgentActivityIndicator
              activity={agentActivity}
              thinkingWord={THINKING_WORDS[thinkingIdx]}
            />
          )}
        </div>
      </div>

      {/* Drag handle to resize input area */}
      <div
        onMouseDown={handleDragStart}
        className="h-1 cursor-row-resize border-t border-[var(--color-ide-border)] hover:bg-[var(--color-ide-accent)] hover:opacity-60 transition-colors"
      />

      {/* Image preview strip */}
      {attachedImages.length > 0 && (
        <div className="px-2 py-1 flex gap-1.5 overflow-x-auto">
          {attachedImages.map((img) => (
            <div key={img.id} className="relative shrink-0 group">
              <img
                src={img.previewUrl}
                alt="Attached"
                className="h-12 w-12 object-cover rounded border border-[var(--color-ide-border)]"
              />
              <button
                onClick={() =>
                  setAttachedImages((prev) =>
                    prev.filter((i) => i.id !== img.id)
                  )
                }
                className="absolute -top-1 -right-1 bg-[var(--color-ide-surface)] rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove image"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Hidden file input for image attachment */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif"
        multiple
        className="hidden"
        onChange={(e) => {
          Array.from(e.target.files ?? []).forEach(processImageFile);
          e.target.value = "";
        }}
      />

      {/* Input row */}
      <div
        className="px-2 pt-1 pb-1"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          Array.from(e.dataTransfer.files).forEach(processImageFile);
        }}
      >
        <div
          className="flex items-center gap-1.5 bg-[var(--color-ide-bg)] border border-[var(--color-ide-border)] rounded-lg px-2 py-1"
          style={{ height: inputHeight }}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              autoResize();
              // Reset history navigation when input is modified. This complements
              // the keydown reset by catching non-keyboard input (paste via context
              // menu, drag-and-drop, IME composition, etc.)
              if (historyPosition.current !== -1) {
                historyPosition.current = -1;
              }
            }}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              const files = Array.from(e.clipboardData.files);
              if (files.length > 0) {
                files.forEach(processImageFile);
              }
            }}
            placeholder="Ask about your design... (Enter to send)"
            rows={1}
            className="flex-1 h-full bg-transparent text-xs text-[var(--color-ide-text)] placeholder-[var(--color-ide-text-dim)] outline-none resize-none font-mono leading-relaxed overflow-y-auto"
            disabled={loading}
          />
          {/* Attach image button */}
          {!loading && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 p-1 rounded text-[var(--color-ide-text-muted)]
                         hover:text-[var(--color-ide-text)] hover:bg-white/5
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Attach image"
            >
              <Paperclip size={14} />
            </button>
          )}
          {/* Voice input button */}
          {!loading && (
            <button
              onClick={toggleRecording}
              className={`shrink-0 p-1 rounded transition-colors hover:bg-white/5 ${
                isRecording
                  ? "text-red-400 hover:text-red-300"
                  : "text-[var(--color-ide-text-muted)] hover:text-[var(--color-ide-text)]"
              }`}
              title={isRecording ? "Stop recording" : "Voice input"}
            >
              <Mic size={14} />
            </button>
          )}
          {/* Cancel button — shown while loading */}
          {loading && (
            <button
              onClick={handleStop}
              disabled={cancellationRequested}
              className="shrink-0 p-1 rounded text-red-400
                         hover:text-red-300 hover:bg-white/5
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Cancel request"
            >
              {cancellationRequested ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <XCircle size={14} />
              )}
            </button>
          )}
          {/* Send button — hidden while loading */}
          {!loading && (
            <button
              onClick={handleSend}
              disabled={!input.trim() && attachedImages.length === 0}
              className="shrink-0 p-1 rounded text-[var(--color-ide-text-muted)]
                         hover:text-[var(--color-ide-text)] hover:bg-white/5
                         disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Send (Enter)"
            >
              <Send size={14} />
            </button>
          )}
        </div>
        <p className="mt-1 text-[10px] text-[var(--color-ide-text-dim)] px-1">
          Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
